import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Card, styled, Typography, useTheme } from '@mui/material';

import { HotelDescriptionDTO, HotelDescriptionTextDTO, HotelCheckOptionDTO } from '@bandido/shared/dtos';
import { InfoLanguage } from '@bandido/shared/enums';

import { getTextToShow } from '@frontend/features/hotel-content';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

interface Props {
  descriptions: HotelDescriptionDTO[],
  checkOptions: HotelCheckOptionDTO[],
}

const MAX_BRIEF_DESCRIPTION_LENGTH = 250;

const AboutCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
}));

export const HotelFaresViewCardAbout:FC<Props> = ({ descriptions, checkOptions }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [showCompleteDescription, setShowCompleteDescription] = useState(false);

  const getTexts = (texts: HotelDescriptionTextDTO[]) => {
    const info = getTextToShow(texts, i18n.language as InfoLanguage);
    if (info.translated) {
      return t(info.text);
    } else {
      return info.text;
    }
  };

  const completeDescription = useMemo(() => descriptions.map(({ texts }) => getTexts(texts)).join('<br/>'), [descriptions]);

  return (
    <AboutCard elevation={4}>
      <Typography variant="h6" color={theme.palette.text.primary}>
        {t('hotels.fares.about')}
      </Typography>
      <Box
        sx={{mt: theme.spacing(2)}}
      >
        <Box
          dangerouslySetInnerHTML={{
            __html: showCompleteDescription ? completeDescription : `${completeDescription.substring(0, 249)}...`
          }}
        />

        {completeDescription.length > MAX_BRIEF_DESCRIPTION_LENGTH && (
          <Button
            variant="text"
            onClick={() => setShowCompleteDescription((prev) => !prev)}
            sx={{ p: 0, textTransform: 'lowercase'}}
          >
            {t(`hotels.fares.showCompleteDescription${showCompleteDescription ? 'False': 'True'}`)}
            {showCompleteDescription ? <ExpandLess/> : <ExpandMore/>}
          </Button>
        )}
      </Box>
      {
        checkOptions.map(({ id, instructions, minAge, endTime, startTime}) => (
          <>
            <Box key={id}>
              <Typography variant='subtitle2'>
                {t('hotels.fares.checkIn')}
              </Typography>
              <Typography variant='body1'>
                {`${startTime}` || t('common.notValueFound')}
              </Typography>
            </Box>
            <Box key={id}>
              <Typography variant='subtitle2'>
                {t('hotels.fares.checkOut')}
              </Typography>
              <Typography variant='body1'>
                {`${endTime}` || t('common.notValueFound')}
              </Typography>
            </Box>
            {
              Boolean(minAge) && (
                <Box>
                  <Typography variant='subtitle2'>
                    {t('hotels.fares.minAge')}
                  </Typography>
                  <Typography variant='body1'>
                    {minAge}
                  </Typography>
                </Box>
              )
            }
            {
              Boolean(instructions?.length) && (
                <Box>
                  <Typography variant='subtitle2'>
                    {t('hotels.fares.checkInstructions')}
                  </Typography>
                  {
                    <Typography key={id} variant='body1'>
                      { getTexts(instructions) }
                    </Typography>
                  }
                </Box>
              )
            }
          </>
        ))
      }
    </AboutCard>
  );
};
